import { Decimal, toDecimal } from '../money/decimal.js';
import { money, type Money } from '../money/money.js';
import { buildPositions, type Position } from './positions.js';
import type { Currency } from '../types/enums.js';
import type { DecimalString, InvestmentTransaction, IsoDate, Uuid } from '../types/domain.js';

/**
 * Where a holding's numbers came from.
 *
 * The two brokers hand us different shapes and pretending otherwise costs
 * accuracy. Lightyear exports a transaction report, so its positions are
 * folded from the ledger. IBKR's Flex Open Positions report is a daily
 * snapshot of quantity and cost basis, so those numbers are taken as reported.
 */
export type HoldingSource = 'ledger' | 'snapshot';

/** A row of `position_snapshots`, as it arrives from the database. */
export interface PositionSnapshot {
  accountId: Uuid;
  instrumentId: Uuid;
  asOf: IsoDate;
  quantity: DecimalString;
  costBasis: DecimalString | null;
  marketValue: DecimalString | null;
  currency: Currency;
  source: string;
}

/** One instrument held in one account, however that was established. */
export interface Holding {
  accountId: Uuid;
  instrumentId: Uuid;
  quantity: Decimal;
  currency: Currency;
  /** Null when the source reports no cost basis. */
  costBasis: Money | null;
  /** What the broker said it was worth, when it said so. */
  reportedValue: Money | null;
  source: HoldingSource;
  /** Snapshot date, or the last trade date for a ledger holding. */
  asOf: IsoDate | null;
}

export function holdingFromPosition(position: Position): Holding {
  return {
    accountId: position.accountId,
    instrumentId: position.instrumentId,
    quantity: position.quantity,
    currency: position.currency,
    costBasis: position.costBasis,
    reportedValue: null,
    source: 'ledger',
    asOf: position.lastTradeDate,
  };
}

/**
 * The newest snapshot per account, expanded into holdings.
 *
 * Only rows carrying that account's newest date are kept, and that is the
 * whole point: a position the broker stops reporting has been closed, and
 * taking "the newest row per instrument" instead would leave it on the
 * dashboard forever at its last known size.
 */
export function latestSnapshotHoldings(snapshots: readonly PositionSnapshot[]): Holding[] {
  const newestDayPerAccount = new Map<Uuid, IsoDate>();
  for (const snapshot of snapshots) {
    const current = newestDayPerAccount.get(snapshot.accountId);
    if (current === undefined || snapshot.asOf > current) {
      newestDayPerAccount.set(snapshot.accountId, snapshot.asOf);
    }
  }

  return snapshots
    .filter((snapshot) => snapshot.asOf === newestDayPerAccount.get(snapshot.accountId))
    .map((snapshot) => ({
      accountId: snapshot.accountId,
      instrumentId: snapshot.instrumentId,
      quantity: toDecimal(snapshot.quantity),
      currency: snapshot.currency,
      costBasis: snapshot.costBasis === null ? null : money(snapshot.costBasis, snapshot.currency),
      reportedValue:
        snapshot.marketValue === null ? null : money(snapshot.marketValue, snapshot.currency),
      source: 'snapshot' as const,
      asOf: snapshot.asOf,
    }));
}

export interface BuildHoldingsInput {
  transactions: readonly InvestmentTransaction[];
  snapshots: readonly PositionSnapshot[];
  /** Accounts absent from the map are treated as ledger accounts. */
  accountSources: ReadonlyMap<Uuid, HoldingSource>;
  /**
   * The newest day the broker actually covered, per account
   * (`accounts.positions_as_of`).
   *
   * This is the only way to see an account that has been sold down to
   * nothing: it reports zero positions, so no rows arrive for that day, and
   * the newest day that *has* rows is still the day before the sale. Accounts
   * absent from the map fall back to the newest day in their own rows.
   */
  snapshotAsOf?: ReadonlyMap<Uuid, IsoDate>;
}

/**
 * Every current holding, from both data paths.
 *
 * An account is read one way or the other, never both: mixing a snapshot with
 * ledger rows for the same account would double-count. Which way is a property
 * of the account (`accounts.position_source`), not a guess made here.
 */
export function buildHoldings({
  transactions,
  snapshots,
  accountSources,
  snapshotAsOf,
}: BuildHoldingsInput): Holding[] {
  const isSnapshotAccount = (accountId: Uuid): boolean =>
    accountSources.get(accountId) === 'snapshot';

  const ledgerHoldings = buildPositions(
    transactions.filter((transaction) => !isSnapshotAccount(transaction.accountId)),
  )
    .filter((position) => !position.quantity.isZero())
    .map(holdingFromPosition);

  const current = snapshots.filter((snapshot) => {
    if (!isSnapshotAccount(snapshot.accountId)) return false;

    // Older than the last day the broker reported on: the broker has stopped
    // listing this position, which is what selling it looks like from here.
    const covered = snapshotAsOf?.get(snapshot.accountId);
    return covered === undefined || snapshot.asOf >= covered;
  });

  const snapshotHoldings = latestSnapshotHoldings(current).filter(
    (holding) => !holding.quantity.isZero(),
  );

  return [...ledgerHoldings, ...snapshotHoldings];
}

export interface InstrumentHoldings {
  instrumentId: Uuid;
  currency: Currency;
  totalQuantity: Decimal;
  /** Null when any contributing holding is missing a cost basis. */
  totalCostBasis: Money | null;
  /** Per account, so "how much of this is at which broker" is answerable. */
  byAccount: Holding[];
}

/**
 * Collapse holdings by instrument, keeping the per-account breakdown.
 *
 * This is the question the spreadsheet could not answer: the same ETF held at
 * two brokers is one exposure and two custodians, and you need both numbers.
 */
export function groupByInstrument(holdings: readonly Holding[]): InstrumentHoldings[] {
  const grouped = new Map<Uuid, Holding[]>();

  for (const holding of holdings) {
    const existing = grouped.get(holding.instrumentId);
    if (existing) existing.push(holding);
    else grouped.set(holding.instrumentId, [holding]);
  }

  return [...grouped.entries()].map(([instrumentId, items]) => {
    // Same instrument, so same trading currency; the first one is representative.
    const currency = items[0]!.currency;

    const totalQuantity = items.reduce(
      (sum, holding) => sum.plus(holding.quantity),
      new Decimal(0),
    );

    // A partial total would understate the position, so it is all or nothing.
    const everyCostKnown = items.every((holding) => holding.costBasis !== null);
    const totalCostBasis = everyCostKnown
      ? money(
          items.reduce((sum, holding) => sum.plus(holding.costBasis!.amount), new Decimal(0)),
          currency,
        )
      : null;

    return {
      instrumentId,
      currency,
      totalQuantity,
      totalCostBasis,
      byAccount: [...items].sort((a, b) => (a.accountId < b.accountId ? -1 : 1)),
    };
  });
}
